import { useEffect, useState } from 'react';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    // ... existing code ...
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#6B8E23] rounded-full">
                <i className="ri-bear-smile-line text-white text-xl"></i>
              </div>
              <span className="text-2xl font-bold text-[#6B8E23]">Pack Walk</span>
            </div>
            <a 
              href="https://lin.ee/efxLxk7" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-[#6B8E23] text-white rounded-full font-semibold hover:bg-[#556B1F] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              今すぐ申し込む
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 bg-cover bg-center" style={{backgroundImage: 'url(https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/2c49f8b0f9cedd3450ab411a561a397c.jpeg)'}}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/10"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full mb-8 shadow-lg">
              <i className="ri-calendar-line text-[#6B8E23] text-lg"></i>
              <span className="text-gray-800 font-semibold">2026年３月7日(土)開催</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              パックウォーク
            </h1>
            
            <p className="text-2xl md:text-3xl text-white mb-4 font-medium">
              愛犬と一緒に、楽しく歩きながら簡単トレーニング。
            </p>
            
            <p className="text-xl text-white/90 mb-12 leading-relaxed">
              飼い主と愛犬がまとまって歩く喜びを学ぶ、特別な1日。<br />
              自然の中で、絆を深めるトレーニング体験。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="#apply" 
                className="px-8 py-4 bg-[#6B8E23] text-white rounded-full font-bold text-lg hover:bg-[#556B1F] transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-clipboard-line text-xl"></i>
                お申し込みはこちら
              </a>
              <a 
                href="#details" 
                className="px-8 py-4 bg-white text-[#6B8E23] rounded-full font-bold text-lg hover:bg-gray-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-information-line text-xl"></i>
                詳細を見る
              </a>
            </div>
            
            <div className="flex flex-wrap gap-6 justify-center">
              <div className="flex items-center gap-2 text-white">
                <i className="ri-bear-smile-line text-2xl"></i>
                <span className="font-semibold">全犬種OK</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <i className="ri-group-line text-2xl"></i>
                <span className="font-semibold">少人数制</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <i className="ri-leaf-line text-2xl"></i>
                <span className="font-semibold">自然の中で</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">パックウォークとは?</h2>
            <p className="text-xl text-gray-600">飼い主と愛犬の絆を深める、まとまって歩く訓練イベント</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="space-y-6">
              <p className="text-xl text-gray-700 leading-relaxed font-medium">
                パックウォークは、飼い主と愛犬が<strong className="text-[#6B8E23]">一緒にまとまって歩く</strong>ことを学ぶ、特別なトレーニングイベントです。
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                集団行動を本能とする犬が複数の犬と飼主が一緒に群れになって歩くことで、問題行動の改善、リハビリにつながるものです。
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                ドッグフォレスト湘南の自然豊かな環境で、経験豊富な講師が丁寧に指導いたします。
              </p>
              
              <div className="space-y-4 pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <span className="text-gray-700 font-medium">愛犬との絆が深まる</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <span className="text-gray-700 font-medium">落ち着いて歩けるようになる</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <span className="text-gray-700 font-medium">他の犬との社会化</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl bg-[#E8F5E9]">
                <img 
                  src="https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/ced8d822a53ab80e81a6c6dd1e4a22ad.jpeg"
                  alt="愛犬と一緒に楽しく学ぶ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#6B8E23] rounded-3xl flex items-center justify-center shadow-xl">
                <i className="ri-bear-smile-line text-6xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              こんな方におすすめ
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-2xl mb-6 mx-auto">
                <i className="ri-run-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">散歩で引っ張られる</h3>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                愛犬が散歩中にぐいぐい引っ張って、落ち着いて歩けない方
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-2xl mb-6 mx-auto">
                <i className="ri-alarm-warning-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">他の犬に吠える</h3>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                散歩中に他の犬を見ると吠えたり興奮してしまう方
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-2xl mb-6 mx-auto">
                <i className="ri-heart-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">絆を深めたい</h3>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                愛犬との信頼関係をより深め、楽しい時間を過ごしたい方
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-2xl mb-6 mx-auto">
                <i className="ri-group-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">社会化を学びたい</h3>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                他の犬や人との接し方を学び、社会性を身につけさせたい方
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-b from-[#F5F5DC]/30 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              料金プラン
            </h2>
            <p className="text-lg text-gray-600">
              お得な2頭割引もご用意しています
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-10 rounded-3xl shadow-xl border-2 border-[#6B8E23]/20 hover:border-[#6B8E23] transition-all">
              <div className="text-center mb-8">
                <div className="w-20 h-20 flex items-center justify-center bg-[#6B8E23]/10 rounded-full mx-auto mb-6">
                  <i className="ri-bear-smile-line text-4xl text-[#6B8E23]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-800">1頭プラン</h3>
                <p className="text-gray-600 text-sm mb-6">初めての方におすすめ</p>
                <div className="text-5xl font-bold text-[#6B8E23] mb-2">¥5,500</div>
                <p className="text-gray-500 text-sm">税込</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B8E23] text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">基本トレーニング</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B8E23] text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">グループウォーキング</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B8E23] text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">個別アドバイス</span>
                </li>
              </ul>
              <a 
                href="#contact" 
                className="block w-full py-4 bg-[#6B8E23] text-white rounded-full font-semibold hover:bg-[#556B1A] transition-all text-center whitespace-nowrap cursor-pointer"
              >
                お申し込み
              </a>
            </div>
            
            <div className="bg-gradient-to-br from-[#6B8E23] to-[#556B1A] p-10 rounded-3xl shadow-2xl relative overflow-hidden">
              <div className="absolute top-6 right-6 bg-[#FFD700] text-gray-800 px-4 py-1.5 rounded-full text-xs font-bold">
                お得
              </div>
              <div className="text-center mb-8">
                <div className="w-20 h-20 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-6">
                  <i className="ri-bear-smile-line text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white">2頭プラン</h3>
                <p className="text-white/90 text-sm mb-6">複数頭飼いの方に</p>
                <div className="text-5xl font-bold text-white mb-2">¥8,250</div>
                <p className="text-white/80 text-sm">税込（2頭目 ¥2,750）</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white text-sm">基本トレーニング（2頭分）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white text-sm">グループウォーキング</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white text-sm">個別アドバイス</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-star-line text-[#FFD700] text-xl mt-0.5"></i>
                  <span className="text-white text-sm font-semibold">2頭目が50%OFF</span>
                </li>
              </ul>
              <a 
                href="#contact" 
                className="block w-full py-4 bg-white text-[#6B8E23] rounded-full font-semibold hover:bg-gray-100 transition-all text-center whitespace-nowrap cursor-pointer"
              >
                お申し込み
              </a>
            </div>
          </div>
          
          <div className="mt-12 max-w-3xl mx-auto bg-white p-8 rounded-2xl shadow-lg">
            <h4 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
              <i className="ri-information-line text-[#6B8E23]"></i>
              開催情報
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">開催日時</p>
                <p className="font-semibold text-gray-800">2026年３月7日(土)</p>
                <p className="text-sm text-gray-600">10:30~ / 13:30~</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">開催場所</p>
                <p className="font-semibold text-gray-800">ドッグフォレスト湘南</p>
                <p className="text-sm text-gray-600">及び近郊の自然豊かなエリア</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              お客様の声
            </h2>
            <p className="text-lg text-gray-600">
              実際に参加された方々の感想をご紹介します
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                  <i className="ri-user-smile-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-800">田中様</p>
                  <p className="text-sm text-gray-600">ゴールデンレトリバー 3歳</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                散歩中に引っ張る癖がひどかったのですが、パックウォークに参加してから落ち着いて歩けるようになりました。講師の方の丁寧な指導のおかげです。愛犬との絆も深まり、参加して本当に良かったです。
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                  <i className="ri-user-smile-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-800">佐藤様</p>
                  <p className="text-sm text-gray-600">柴犬 2歳</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                他の犬に吠えてしまう問題に悩んでいましたが、グループウォーキングを通じて社会化を学ぶことができました。自然の中でのトレーニングは愛犬もリラックスできて、とても効果的でした。
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#F5F5DC] to-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-full flex-shrink-0">
                  <i className="ri-user-smile-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-800">鈴木様</p>
                  <p className="text-sm text-gray-600">トイプードル 1歳</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
                <i className="ri-star-fill text-[#FFD700] text-lg"></i>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                初めての参加で不安でしたが、少人数制で丁寧に教えていただけました。同じ悩みを持つ飼い主さんとも交流でき、とても有意義な時間でした。また参加したいです！
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-[#F5F5DC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              パックウォークの様子
            </h2>
            <p className="text-lg text-gray-600">
              実際のイベントの雰囲気をご覧ください
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="relative group overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="aspect-[4/3] w-full h-full">
                <img 
                  src="https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/8c6d4b9970e24cd370878d648515a8a6.jpeg"
                  alt="パックウォークの様子1"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white font-semibold text-lg">みんなで楽しくウォーキング</p>
              </div>
            </div>
            
            <div className="relative group overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="aspect-[4/3] w-full h-full">
                <img 
                  src="https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/ced8d822a53ab80e81a6c6dd1e4a22ad.jpeg"
                  alt="パックウォークの様子2"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white font-semibold text-lg">自然の中でリラックス</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instructors Section */}
      <section id="instructors" className="py-24 bg-[#F5F5DC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">講師紹介</h2>
            <p className="text-xl text-gray-600">経験豊富なプロフェッショナルが丁寧に指導します</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="aspect-square bg-[#E8F5E9]">
                <img 
                  src="https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/ad7d0d8f810f66f7560d209dabd2adfc.jpeg"
                  alt="平木貴恵トレーナー"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">平木 貴恵</h3>
                <p className="text-[#6B8E23] font-semibold mb-4">ドッグトレーナー</p>
                <p className="text-gray-600 leading-relaxed">
                  豊富な経験と深い愛情で、飼い主さんと愛犬の関係性を大切にした指導を行います。一頭一頭の個性を見極め、その子に合ったアプローチを提案します。
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="aspect-square bg-[#E8F5E9]">
                <img 
                  src="https://static.readdy.ai/image/2279cd1ae7c10b0c65dbf6269d3b6caf/02bf2b44cc59bf35ead5a6640e48aed6.jpeg"
                  alt="原田雅美トレーナー"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">原田 雅美</h3>
                <p className="text-[#6B8E23] font-semibold mb-4">ドッグトレーナー</p>
                <p className="text-gray-600 leading-relaxed">
                  優しく丁寧な指導で、初心者の方にも分かりやすくトレーニング方法をお伝えします。楽しく学べる雰囲気作りを心がけています。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gradient-to-b from-[#F5F5DC]/30 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              よくある質問
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: '初心者でも参加できますか?',
                answer: 'はい、もちろんです！初心者の方から経験者の方まで、どなたでもご参加いただけます。講師が丁寧にサポートいたしますので、安心してご参加ください。'
              },
              {
                question: '子犬でも参加できますか?',
                answer: 'ワクチン接種が完了していれば、子犬でもご参加いただけます。ただし、月齢や状態によっては個別にご相談させていただく場合がございますので、お申し込み時にお知らせください。'
              },
              {
                question: '雨天の場合はどうなりますか?',
                answer: '小雨の場合は開催いたしますが、荒天の場合は延期または中止となる場合がございます。当日の天候による変更は、事前にお電話またはLINEにてご連絡いたします。'
              },
              {
                question: '持ち物は何が必要ですか?',
                answer: '普段使用しているリード、首輪またはハーネス、おやつ、お水、ウンチ袋をお持ちください。また、飼い主様は動きやすい服装と歩きやすい靴でお越しください。'
              },
              {
                question: 'キャンセルはできますか?',
                answer: 'キャンセルの場合は、できるだけ早めにご連絡ください。キャンセルポリシーについては、お申し込み時に詳しくご説明いたします。'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#6B8E23]/10 rounded-full flex-shrink-0">
                      <i className="ri-question-line text-[#6B8E23] text-xl"></i>
                    </div>
                    <h3 className="font-bold text-gray-800 text-base">{faq.question}</h3>
                  </div>
                  <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6 pl-[88px]">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#F5F5DC]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              愛犬との絆を深める<br />特別な一日を体験しませんか?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              定員に達し次第、受付終了となります。お早めにお申し込みください。
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="w-16 h-16 flex items-center justify-center bg-[#6B8E23] rounded-full mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">お電話でのお申し込み</h3>
              <p className="text-gray-600 text-center mb-6 text-sm">お気軽にお電話ください</p>
              <a 
                href="tel:0463-59-7611" 
                className="block w-full py-4 bg-[#6B8E23] text-white rounded-full font-semibold hover:bg-[#556B1A] transition-all text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill mr-2"></i>
                電話をかける
              </a>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="w-16 h-16 flex items-center justify-center bg-[#00B900] rounded-full mx-auto mb-6">
                <i className="ri-line-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">LINEでのお申し込み</h3>
              <p className="text-gray-600 text-center mb-6 text-sm">24時間受付中</p>
              <a 
                href="https://lin.ee/efxLxk7" 
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-4 bg-[#00B900] text-white rounded-full font-semibold hover:bg-[#009900] transition-all text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-line-fill mr-2"></i>
                LINEで申し込む
              </a>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h4 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-[#6B8E23]"></i>
              お申し込み時にお伝えください
            </h4>
            <ul className="grid md:grid-cols-2 gap-3 text-gray-700 text-sm">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-[#6B8E23]"></i>
                参加者様のお名前
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-[#6B8E23]"></i>
                お電話番号
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-[#6B8E23]"></i>
                愛犬のお名前・犬種・年齢
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-[#6B8E23]"></i>
                参加頭数(1頭 or 2頭)
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6B8E23] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                <i className="ri-bear-smile-line text-[#6B8E23] text-xl"></i>
              </div>
              <span className="text-2xl font-bold">Pack Walk</span>
            </div>
            <p className="text-white/80 mb-2">愛犬と飼い主の絆を深める、まとまって歩く訓練イベント</p>
            <p className="text-white/80 mb-6">ドッグフォレスト湘南</p>
            
            <div className="flex gap-4 justify-center mb-8">
              <a href="https://www.facebook.com/dogrun.shonan/?locale=ja_JP" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 cursor-pointer">
                <i className="ri-facebook-fill text-2xl"></i>
              </a>
              <a href="https://www.instagram.com/dogforestshonan/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 cursor-pointer">
                <i className="ri-instagram-line text-2xl"></i>
              </a>
              <a href="https://lin.ee/efxLxk7" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 cursor-pointer">
                <i className="ri-line-fill text-2xl"></i>
              </a>
            </div>
            
            <div className="border-t border-white/20 pt-6">
              <p className="text-white/60 text-sm">© 2025 Pack Walk - Dog Forest Shonan. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-[#6B8E23] text-white rounded-full shadow-xl hover:bg-[#556B1F] transition-all duration-300 flex items-center justify-center hover:scale-110 z-40 cursor-pointer"
      >
        <i className="ri-arrow-up-line text-2xl"></i>
      </button>
    </div>
  );
}
